/**
 * App Providers
 *
 * Wraps the application with all necessary providers:
 * - Google OAuth
 * - Stripe
 * - reCAPTCHA
 */

import { useEffect, type ReactNode } from 'react'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { apiConfig } from '../api/config'

// Initialize Stripe
const stripePromise = apiConfig.stripePublicKey ? loadStripe(apiConfig.stripePublicKey) : null

interface AppProvidersProps {
  children: ReactNode
}

/**
 * Google OAuth Provider wrapper
 * Only renders if client ID is configured
 */
function GoogleAuthWrapper({ children }: { children: ReactNode }) {
  if (!apiConfig.googleClientId) {
    return <>{children}</>
  }

  return <GoogleOAuthProvider clientId={apiConfig.googleClientId}>{children}</GoogleOAuthProvider>
}

/**
 * Stripe Provider wrapper
 * Only renders if public key is configured
 */
function StripeWrapper({ children }: { children: ReactNode }) {
  if (!stripePromise) {
    return <>{children}</>
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        appearance: {
          theme: 'stripe',
        },
      }}
    >
      {children}
    </Elements>
  )
}

/**
 * reCAPTCHA Provider wrapper
 * Only renders if site key is configured
 */
function ReCaptchaWrapper({ children }: { children: ReactNode }) {
  if (!apiConfig.recaptchaSiteKey) {
    return <>{children}</>
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={apiConfig.recaptchaSiteKey}>
      {children}
    </GoogleReCaptchaProvider>
  )
}

/**
 * Apple Sign-In Provider wrapper
 * Loads the Apple JS SDK when client ID is configured
 */
function AppleAuthWrapper({ children }: { children: ReactNode }) {
  if (!apiConfig.appleClientId) {
    return <>{children}</>
  }

  useEffect(() => {
    const script = document.createElement('script')
    script.src =
      'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
    script.async = true
    script.onload = () => {
      ;(window as any).AppleID?.auth.init({
        clientId: apiConfig.appleClientId,
        scope: 'name email',
        redirectURI: apiConfig.appleRedirectUri || window.location.origin,
        usePopup: true,
      })
    }
    document.head.appendChild(script)
    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return <>{children}</>
}

/**
 * Combined App Providers
 */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <GoogleAuthWrapper>
      <AppleAuthWrapper>
        <StripeWrapper>
          <ReCaptchaWrapper>{children}</ReCaptchaWrapper>
        </StripeWrapper>
      </AppleAuthWrapper>
    </GoogleAuthWrapper>
  )
}

// Re-export hooks for easy access
export { useGoogleLogin, GoogleLogin } from '@react-oauth/google'
export { useStripe, useElements, CardElement, PaymentElement } from '@stripe/react-stripe-js'
export { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
